import { useEffect, useMemo } from "react";
import {
    formatDemandPlanningDate,
    PRESET_COLUMN_IDS,
    dedupeDemandPlanningColumnIds,
    getDemandPlanningColumnLabel,
    defaultWidthForColumn,
} from "../../consts/demandplanning";
import { arrayHasItems } from "../../helpers/common";
import { TotalsCashLiabilities } from "./cashLiabilitiesTableComponents/TotalsCashLiabilities";
import {
    formatPurchasePlanningCashRequired,
    formatPurchasePlanningCurrency,
} from "../../lib/purchasePlanningHelpers";
import { useDispatch, useSelector } from "react-redux";
import { DemandPlanningLoadingKeys, getCashLiabilitiesTableData } from "../../slices/demandPlanningSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { HeaderCellDefaultTable } from "./defaultPlanningTableComponents/HeaderCellDefaultTable";

const getApprovalTone = (value) => {
    if (value === true) return "border-transparent bg-emerald-50 text-emerald-700";
    if (value === false) return "border-transparent bg-slate-100 text-slate-700";
    return "border-transparent bg-amber-50 text-amber-700";
};

const getApprovalLabel = (value) => {
    if (value === true) return "Approved";
    if (value === false) return "Not approved";
    return "Unapproved";
};

const getDaysUntilDue = (dateValue) => {
    if (!dateValue) return null;
    const dueDate = new Date(dateValue);
    if (Number.isNaN(dueDate.getTime())) return null;
    const now = new Date();
    const diffMs = dueDate.getTime() - now.getTime();
    return Math.round(diffMs / (24 * 60 * 60 * 1000));
};

const SUPPLIER_PENDING_LABEL = "Supplier pending";

const normalizeSupplierName = (value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed || trimmed === "-") return SUPPLIER_PENDING_LABEL;
    return trimmed;
};

const getTimingBucket = (daysUntilDue) => {
    if (daysUntilDue === null) return "Date pending";
    if (daysUntilDue < 0) return "Past due";
    if (daysUntilDue <= 30) return "Next 30 days";
    if (daysUntilDue <= 60) return "31–60 days";
    if (daysUntilDue <= 90) return "61–90 days";
    return "90+ days";
};

const CashLiabilitiesTable = ({
    selectedForecastDemandPlanning,
    visibleColumnIds: visibleColumnIdsProp,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
}) => {
    const dispatch = useDispatch();

    const {
        loadingKeys,
        cashLiabilitiesTableData,
    } = useSelector(state => state.demandPlanning);

    const cashLiabilitiesTableLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getCashLiabilitiesTableData
    );

    const columnIds = useMemo(() => {
        const fromPreset = dedupeDemandPlanningColumnIds(PRESET_COLUMN_IDS["cash-liabilities"]);
        if (visibleColumnIdsProp === undefined || visibleColumnIdsProp === null) {
            return fromPreset;
        }
        const allow = new Set(visibleColumnIdsProp);
        return fromPreset.filter((id) => allow.has(id));
    }, [visibleColumnIdsProp]);

    const visibleColumns = useMemo(
        () =>
            columnIds.map((id) => ({
                id,
                label: getDemandPlanningColumnLabel(id),
                width: defaultWidthForColumn(id),
            })),
        [columnIds]
    );

    const liabilityRows = useMemo(() => {
        const rows = cashLiabilitiesTableData?.liabilityRows;
        return arrayHasItems(rows)
            ? rows.map((row) => {
                const daysUntilDue = getDaysUntilDue(row.buyDate);
                return {
                    ...row,
                    supplier: normalizeSupplierName(row.supplier),
                    daysUntilDue,
                    timingBucket: getTimingBucket(daysUntilDue),
                };
            })
            : [];
    }, [cashLiabilitiesTableData]);

    const countOfRowsInScope = liabilityRows.length;

    const { suppliersList, totals } = useMemo(() => {
        const supplierMap = new Map();
        liabilityRows.forEach((row) => {
            const key = normalizeSupplierName(row.supplier);
            if (!supplierMap.has(key)) {
                supplierMap.set(key, {
                    name: key,
                    totalLiability: 0,
                    rowCount: 0,
                    approvedCount: 0,
                });
            }
            const entry = supplierMap.get(key);
            entry.totalLiability += Number(row.liabilityValue || 0);
            entry.rowCount += 1;
            if (row.approvalStatus === true) entry.approvedCount += 1;
        });

        const suppliersListSorted = Array.from(supplierMap.values()).sort((left, right) => {
            return right.totalLiability - left.totalLiability || left.name.localeCompare(right.name);
        });

        const topSupplier = suppliersListSorted[0];
        const largestSupplierName = topSupplier?.name ?? null;
        const largestSupplierValue = Number(topSupplier?.totalLiability ?? 0);

        const totalLiability = liabilityRows.reduce((sum, row) => sum + Number(row.liabilityValue || 0), 0);
        const dueNext30 = liabilityRows
            .filter((row) => row.daysUntilDue !== null && row.daysUntilDue >= 0 && row.daysUntilDue <= 30)
            .reduce((sum, row) => sum + Number(row.liabilityValue || 0), 0);
        const due31To60 = liabilityRows
            .filter((row) => row.daysUntilDue !== null && row.daysUntilDue >= 31 && row.daysUntilDue <= 60)
            .reduce((sum, row) => sum + Number(row.liabilityValue || 0), 0);
        const due61To90 = liabilityRows
            .filter((row) => row.daysUntilDue !== null && row.daysUntilDue >= 61 && row.daysUntilDue <= 90)
            .reduce((sum, row) => sum + Number(row.liabilityValue || 0), 0);
        const unapproved = liabilityRows
            .filter((row) => row.approvalStatus !== true)
            .reduce((sum, row) => sum + Number(row.liabilityValue || 0), 0);

        const totalsRow = [
            { label: "Total cash committed", value: totalLiability },
            { label: "Due in 30 days", value: dueNext30 },
            { label: "Due in 31-60 days", value: due31To60 },
            { label: "Due in 61-90 days", value: due61To90 },
            {
                label: "Largest supplier exposure",
                supplierName: largestSupplierName,
                value: largestSupplierValue,
            },
            { label: "Unapproved liability", value: unapproved },
        ];

        return { suppliersList: suppliersListSorted, totals: totalsRow };
    }, [liabilityRows]);

    const renderLiabilityCell = (column, row) => {
        const alignRight =
            column.id === "unitsToOrder" ||
            column.id === "unitCost" ||
            column.id === "extendedCashLiability";

        const inner = (() => {
            switch (column.id) {
                case "buyDate":
                    return formatDemandPlanningDate(row.buyDate);
                case "supplier":
                    return (
                        <div>
                            <p className="font-medium text-primary">{row.supplier}</p>
                            <p className="mt-1 text-xs text-gray-600">
                                {[row.category, row.subCategory].filter(Boolean).join(" · ")}
                            </p>
                        </div>
                    );
                case "productName":
                    return <span className="text-primary">{row.productName}</span>;
                case "supplySku":
                    return (
                        <span className="bg-gray-100 text-primary px-2 py-1 rounded-md text-xs border border-gray-200 whitespace-nowrap">
                            {row.supplySku}
                        </span>
                    );
                case "unitsToOrder":
                    return (
                        <span className="font-medium text-primary">
                            {row.unitsToOrder.toFixed(0)}
                        </span>
                    );
                case "unitCost":
                    return (
                        <span className="text-primary">
                            {formatPurchasePlanningCurrency(row.unitCost)}
                        </span>
                    );
                case "extendedCashLiability":
                    return (
                        <span className="font-semibold text-primary">
                            {formatPurchasePlanningCashRequired(row.liabilityValue)}
                        </span>
                    );
                case "approvalStatus":
                    return (
                        <>
                            <span
                                className={`rounded-md px-2 py-1 text-[11px] font-medium shadow-none whitespace-nowrap ${getApprovalTone(row.approvalStatus)}`}
                            >
                                {getApprovalLabel(row.approvalStatus)}
                            </span>
                            {row.approvedBy ? (
                                <p className="mt-1 text-xs text-gray-600">{row.approvedBy}</p>
                            ) : null}
                        </>
                    );
                case "timingBucket":
                    return (
                        <>
                            <p className="text-primary">{row.timingBucket}</p>
                            <p className="mt-1 text-xs text-gray-600">
                                {row.daysUntilDue === null
                                    ? "No buy date"
                                    : row.daysUntilDue < 0
                                      ? `${Math.abs(row.daysUntilDue)} days overdue`
                                      : `${row.daysUntilDue} days out`}
                            </p>
                        </>
                    );
                case "notes":
                    return (
                        <p className="line-clamp-2 max-w-[260px]">{row.notes || "No notes"}</p>
                    );
                default:
                    return "—";
            }
        })();

        return (
            <td
                key={column.id}
                className={`border-b border-transparent px-3 py-3 group-hover:border-border text-sm ${
                    alignRight ? "text-right" : ""
                }`}
            >
                {inner}
            </td>
        );
    };

    useEffect(() => {
        const demandPlanningId = selectedForecastDemandPlanning?.id;
        const demandPlanningYear =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;

        if (!demandPlanningId) return;

        dispatch(
            getCashLiabilitiesTableData({
                demandPlanningId,
                demandPlanningYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            })
        );
    }, [dispatch, selectedForecastDemandPlanning, searchTerm, selectedCategoryId, selectedSubcategoryId, selectedStatusId]);

    if (cashLiabilitiesTableLoading) {
        return (
            <section className="flex-1 px-4 py-4 lg:px-6">
                <div className="flex justify-center items-center h-full mt-8">
                    <LoaderSpinner
                        width="80"
                        height="80"
                        border="5"
                        colorSpinner="gray"
                    />
                </div>
            </section>
        );
    }

    return (
        <section
            className="flex-1 py-4"
        >
            <div className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 border-b border-border pb-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                Cash Liabilities
                            </span>
                        </div>
                        <h2 className="text-xl font-semibold tracking-[-0.02em] text-primary">
                            Cash exposure across approved and upcoming buys
                        </h2>
                        <p className="mt-1 max-w-3xl text-sm text-gray-600">
                            Review committed buy exposure by supplier, timing, and SKU so finance and planning can see where cash pressure is building.
                        </p>
                    </div>
                    <div className="rounded-md border border-border bg-background px-3 py-2 text-right">
                        <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                            Rows in scope
                        </p>
                        <p className="mt-1 text-lg font-semibold text-primary">
                            {countOfRowsInScope}
                        </p>
                    </div>
                </div>
                <TotalsCashLiabilities totals={totals} />
                <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(260px,0.8fr)_minmax(0,1.6fr)]">
                    <aside className="rounded-lg border border-border bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-primary">Supplier exposure</h3>
                                <p className="mt-1 text-xs text-gray-600">Suppliers are ranked by total cash liability in the current filter scope.</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2 max-h-[850px] overflow-y-auto">
                            {arrayHasItems(suppliersList) ? (
                                suppliersList.map((supplier) => {
                                    return (
                                        <div key={supplier.name} className="rounded-md border border-border bg-white px-3 py-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-primary whitespace-break-spaces">{supplier.name}</p>
                                                    <p className="mt-1 text-xs text-gray-600 whitespace-break-spaces">{supplier.rowCount} buy row{supplier.rowCount === 1 ? "" : "s"} · {supplier.approvedCount} approved</p>
                                                </div>
                                                <p className="text-sm font-semibold text-primary">{formatPurchasePlanningCashRequired(supplier.totalLiability)}</p>
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600 whitespace-break-spaces">
                                    No liabilities are in scope for the current filters.
                                </div>
                            )}
                        </div>
                    </aside>
                    <div className="overflow-hidden rounded-lg border border-border bg-white">
                        <div className="max-h-[calc(100vh-16rem)] overflow-auto thin-scrollbar">
                            <table
                                className="min-w-full border-separate border-spacing-0 text-sm"
                            >
                                <thead className="sticky top-0 z-10 bg-white">
                                    <tr>
                                        {visibleColumns.map((column) => {
                                            const numericHeader =
                                                column.id === "unitsToOrder" ||
                                                column.id === "unitCost" ||
                                                column.id === "extendedCashLiability";
                                            return (
                                                <HeaderCellDefaultTable
                                                    key={column.id}
                                                    className={numericHeader ? "text-right" : "text-left"}
                                                >
                                                    {column.label}
                                                </HeaderCellDefaultTable>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {arrayHasItems(liabilityRows) ? (
                                        liabilityRows.map((row) => (
                                            <tr key={row.id} className="group hover:bg-gray-50">
                                                {visibleColumns.map((column) =>
                                                    renderLiabilityCell(column, row)
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                className="px-4 py-8 text-sm text-gray-600 text-center whitespace-break-spaces"
                                                colSpan={Math.max(visibleColumns.length, 1)}
                                            >
                                                No cash liabilities are available in the current filter scope.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CashLiabilitiesTable;